/* eslint-disable react-refresh/only-export-components */
import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Bell, CreditCard, LogOut, Package, ChevronDown } from 'lucide-react';
import { useAuthStore } from '../lib/auth-store';
import { useAccountStore } from '../lib/account-store';
import { clearDashboardSession } from '../hooks/useSessionState';
import { useClickOutside } from '../hooks/useClickOutside';
import { useNotificationStore } from '../lib/notification-store';
import { useUnreadCountQuery } from '../api/notifications';
import { useNotificationStream } from '../hooks/useNotificationStream';
import NotificationDropdown from './notification-dropdown';
import DaaterLogoHomeLink from './daater-logo-home-link';
import { registerPushNotifications, unregisterPushNotifications } from '../lib/push-registration';
import { logoutRequest } from '../api/endpoints';

type AppHeaderContextValue = {
  headerLeft: React.ReactNode | null;
  setHeaderLeft: (node: React.ReactNode | null) => void;
  headerVisible: boolean;
  setHeaderVisible: (visible: boolean) => void;
};

const AppHeaderContext = React.createContext<AppHeaderContextValue | undefined>(undefined);

export const useAppHeader = () => {
  const context = React.useContext(AppHeaderContext);
  if (!context) {
    throw new Error('useAppHeader must be used within AppShell');
  }
  return context;
};

type AppShellProps = {
  children: React.ReactNode;
  title?: string;
};

export default function AppShell({ children, title }: AppShellProps) {
  const user = useAuthStore(state => state.user);
  const clearAuth = useAuthStore(state => state.clearAuth);
  const clearAccount = useAccountStore(state => state.clearAccount);
  const account = useAccountStore(state => state.account);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [headerLeft, setHeaderLeft] = React.useState<React.ReactNode | null>(null);
  const [headerVisible, setHeaderVisible] = React.useState(true);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const unreadCount = useNotificationStore(s => s.unreadCount);
  const [notifOpen, setNotifOpen] = React.useState(false);
  const notifRef = React.useRef<HTMLDivElement | null>(null);
  useUnreadCountQuery();
  useNotificationStream();
  React.useEffect(() => {
    void registerPushNotifications();
  }, []);
  const userData = account?.userData;
  const userDataRecord = userData as Record<string, unknown> | undefined;
  const userFirstName =
    typeof userDataRecord?.firstName === 'string' ? userDataRecord.firstName : '';
  const userLastName = typeof userDataRecord?.lastName === 'string' ? userDataRecord.lastName : '';
  const userFullName = `${userFirstName} ${userLastName}`.trim();
  const resolvedEmail = user?.email ?? userData?.email ?? '';
  const displayName =
    userFullName ||
    (typeof user?.name === 'string' && user.name.trim() ? user.name.trim() : '') ||
    resolvedEmail ||
    'Usuario';
  const initials = displayName.slice(0, 2).toUpperCase();
  const accountPlan = account?.plan ?? account?.teamData?.plan;
  const accountDaysLeft = account?.daysLeft ?? account?.teamData?.daysLeft;
  const accountEmail = resolvedEmail || 'Usuario';
  const headerContextValue = React.useMemo(
    () => ({ headerLeft, setHeaderLeft, headerVisible, setHeaderVisible }),
    [headerLeft, headerVisible]
  );

  useClickOutside(menuRef, () => setMenuOpen(false));
  useClickOutside(notifRef, () => setNotifOpen(false));

  // Va extraído y no inline en `onClick`: un handler `async` devuelve una
  // promesa donde se espera `void`, y su rechazo quedaría sin dueño. El orden y
  // el comportamiento ante fallo son los de antes, a propósito.
  const handleLogout = async () => {
    setMenuOpen(false);
    // La suscripción push se borra mientras la sesión sigue viva.
    await unregisterPushNotifications();
    await logoutRequest();
    clearAuth();
    clearAccount();
    queryClient.clear();
    clearDashboardSession();
    void navigate({ to: '/login' });
  };

  return (
    <AppHeaderContext.Provider value={headerContextValue}>
      <div className="min-h-screen bg-pale-grey text-charcoal-grey">
        {headerVisible ? (
          <header className="bg-white border-b border-pale-grey-three">
            <div className="flex items-center justify-between h-16 px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
              <div className="flex flex-col">
                <DaaterLogoHomeLink
                  alt="Daater logo"
                  ariaLabel="Ir al dashboard"
                  className="inline-flex w-fit"
                  imgClassName="h-8"
                />
                {title ? (
                  <span className="text-xs font-semibold uppercase tracking-[0.3em] text-slate">
                    {title}
                  </span>
                ) : null}
              </div>
              <div className="flex items-center gap-4 text-sm text-slate">
                {headerLeft ? (
                  <>
                    <div className="flex items-center">{headerLeft}</div>
                    <div className="hidden w-px h-6 bg-pale-grey-three sm:block" />
                  </>
                ) : null}
                <div className="relative" ref={notifRef}>
                  <button
                    type="button"
                    aria-label="Notificaciones"
                    onClick={() => setNotifOpen(prev => !prev)}
                    className="relative flex items-center justify-center w-9 h-9 rounded-lg text-slate hover:bg-pale-grey transition"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute top-1 right-1 flex items-center justify-center w-4 h-4 text-[9px] font-bold text-white bg-red-500 rounded-full">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                  {notifOpen && <NotificationDropdown onClose={() => setNotifOpen(false)} />}
                </div>
                <div className="relative" ref={menuRef}>
                  <button
                    type="button"
                    onClick={() => setMenuOpen(prev => !prev)}
                    className="flex items-center gap-3 px-2 py-1 text-sm transition rounded-lg text-slate hover:bg-pale-grey"
                    aria-haspopup="true"
                    aria-expanded={menuOpen}
                  >
                    <div className="flex items-center justify-center text-xs font-semibold bg-blue-100 border border-blue-200 rounded-full h-9 w-9 text-primary">
                      {initials}
                    </div>
                    <div className="flex-col items-start hidden leading-tight sm:flex">
                      <span className="text-sm font-medium text-charcoal-grey">{displayName}</span>
                      <span className="text-xs text-cool-grey">Admin</span>
                    </div>
                    <ChevronDown className="hidden w-4 h-4 text-cool-grey sm:block" />
                  </button>

                  {menuOpen ? (
                    <div
                      className="absolute right-0 z-50 p-4 bg-white border shadow-xl top-12 w-80 rounded-xl border-pale-grey-three"
                      role="menu"
                    >
                      <div className="pb-4 mb-4 border-b border-pale-grey-three">
                        <p className="text-sm font-semibold text-charcoal-grey">{displayName}</p>
                        <div className="mt-3 overflow-hidden border rounded-lg border-pale-grey-three">
                          <div className="grid grid-cols-3 text-xs">
                            <div className="border-b col-span-1 border-pale-grey-three bg-pale-grey p-1.5 font-semibold text-slate">
                              Usuario
                            </div>
                            <div className="border-b col-span-2 border-pale-grey-three p-1.5 text-charcoal-grey">
                              {accountEmail}
                            </div>
                            <div className="border-b col-span-1 border-pale-grey-three bg-pale-grey p-1.5 font-semibold text-slate">
                              Plan
                            </div>
                            <div className="border-b col-span-2 border-pale-grey-three p-1.5 text-charcoal-grey">
                              {accountPlan?.name ?? '-'}
                            </div>
                            <div className="bg-pale-grey col-span-1 p-1.5 font-semibold text-slate">
                              Días restantes
                            </div>
                            <div className="p-1.5 col-span-1 text-charcoal-grey">
                              {typeof accountDaysLeft === 'number' ? accountDaysLeft : '-'}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                          <a
                            className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium transition border rounded-lg border-pale-grey-three text-primary hover:border-primary/40 hover:bg-primary/5"
                            href="https://daater.com/precios-y-planes/"
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Package className="w-4 h-4" />
                            Planes
                          </a>
                          <button
                            type="button"
                            className="flex items-center justify-center w-full gap-2 px-3 py-2 text-sm font-medium transition border rounded-lg border-pale-grey-three text-slate hover:border-primary/40 hover:bg-primary/5"
                            onClick={() => {
                              setMenuOpen(false);
                              void navigate({ to: '/payment-history' });
                            }}
                          >
                            <CreditCard className="w-4 h-4" />
                            Pagos
                          </button>
                        </div>
                        <button
                          type="button"
                          className="flex items-center justify-start w-full gap-2 px-3 py-2 text-sm font-medium transition border rounded-lg border-darkish-pink/30 text-darkish-pink hover:bg-darkish-pink/5"
                          onClick={() => void handleLogout()}
                        >
                          <LogOut className="w-4 h-4 mr-2" />
                          Cerrar sesion
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </header>
        ) : null}
        <main className="w-full mx-auto">{children}</main>
      </div>
    </AppHeaderContext.Provider>
  );
}
